import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { MapHelperService } from 'src/services/map-helper.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-annotation-searcher',
  templateUrl: './annotation-searcher.component.html',
  styleUrls: ['./annotation-searcher.component.css']
})
export class AnnotationSearcherComponent {

  @Input() isSearchDisabled: boolean = false;
  @Input() sourceName: string;
  @Input() displayAttribute: string = '';

  @Output() searchLocationChange = new EventEmitter<string>();
  @Output() searchTotal = new EventEmitter<string>();
  @Output() dropdownVisibilityChange = new EventEmitter<boolean>();

  inputFocused: boolean = false;
  showSuggestions: boolean = false;

  locationHash: Map<string, { lng: number, lat: number }> = new Map();
  locationIdentifier: string = '';

  showError: boolean = false;

  locationSuggestions: { identifier: string, lng: number, lat: number }[] = [];
  dropdownAbove: boolean = false;
  minHeight = 200;
  private searchSubject = new Subject<string>();

  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  constructor(private mapHelperService: MapHelperService) {
    this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(searchTerm => {
      if (!environment.production) console.log('annotation-searcher debounce', searchTerm);
      this.locationIdentifier = searchTerm;
      this.searchLocationChange.emit(this.locationIdentifier);
      this.runSearch();
    });
  }

  ngOnInit(): void {
    this.locationHash = this.getSourceLocations() ?? new Map();
    this.suggestFirstLocations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['sourceName'] || changes['displayAttribute']) && !(changes['sourceName']?.firstChange)) {
      this.locationHash = this.getSourceLocations() ?? new Map();
      this.locationIdentifier = '';
      this.locationSuggestions = [];
      this.showSuggestions = false;
      this.searchLocationChange.emit(this.locationIdentifier);
      this.suggestFirstLocations();
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  getSourceLocations() {
    if (!this.sourceName) return null;

    const source = this.mapHelperService.GetSourceByName(this.sourceName) as any;
    if (!environment.production) console.log('annotation-searcher source', source);

    if (source && source._data && source._data.features) {
      const locations = new Map<string, { lng: number, lat: number }>();
      const idProperty = this.displayAttribute && source._data.features[0]?.properties[this.displayAttribute]  ? this.displayAttribute : '_Id';

      for (const feature of source._data.features) {
        if (
          feature.properties &&
          feature.properties[idProperty] &&
          feature.geometry &&
          feature.geometry.type === 'Point'
        ) {
          const coords = feature.geometry.coordinates;
          locations.set(feature.properties[idProperty], { lng: coords[0], lat: coords[1] });
        }
      }
      this.searchTotal.emit(locations.size.toString());
      return locations;
    }
    this.searchTotal.emit('0');
    return null;
  }

  suggestFirstLocations() {
    if (this.locationHash.size > 0) {
      for (const [identifier, coords] of this.locationHash.entries()) {
        this.locationSuggestions.push({ identifier, lng: coords.lng, lat: coords.lat });
        if (this.locationSuggestions.length >= 5) break;
      }
    }
  }

  onSearchInput() {
    this.searchSubject.next(this.locationIdentifier);
  }

  runSearch() {
    if (this.locationIdentifier) {
      const suggestions = [];
      const searchLower = this.locationIdentifier.toLowerCase();
      for (const [identifier, coords] of this.locationHash.entries()) {
        if (identifier.toLowerCase().includes(searchLower)) {
          suggestions.push({ identifier, lng: coords.lng, lat: coords.lat });
          if (suggestions.length >= 5) break;
        }
      }
      this.locationSuggestions = suggestions;
      this.showSuggestions = suggestions.length > 0;
    } else {
      this.locationSuggestions = [];
      this.showSuggestions = false;
      this.suggestFirstLocations();
    }
    this.updateDropdownVisibility();
  }

  searchLocation() {
    if (this.locationHash.has(this.locationIdentifier)) {
      this.selectSuggestion({ identifier: this.locationIdentifier, ...this.locationHash.get(this.locationIdentifier) });
    } else {
      this.showError = true;
      setTimeout(() => { this.showError = false; }, 3000);
    }
  }

  onInputFocus(): void {
    this.checkDropdownDirection();
    this.inputFocused = true;
    if (this.locationSuggestions.length > 0) {
      this.showSuggestions = true;
    }
    this.updateDropdownVisibility();
  }

  onInputNoFocus(): void {
    setTimeout(() => {
      this.inputFocused = false;
      this.showSuggestions = false;
      this.updateDropdownVisibility();
    }, 200);
  }

  private checkDropdownDirection(): void {
    if (!this.inputRef) 
        return;
    const rect = this.inputRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.dropdownAbove = spaceBelow < this.minHeight;
  }

  private updateDropdownVisibility(): void {
    const isVisible = this.showSuggestions && this.inputFocused && this.locationSuggestions.length > 0;
    this.dropdownVisibilityChange.emit(isVisible);
  }

  selectSuggestion(suggestion: { identifier: string, lng: number, lat: number }): void {
    this.locationIdentifier = suggestion.identifier;
    this.searchLocationChange.emit(this.locationIdentifier);
    this.showSuggestions = false;
    this.updateDropdownVisibility();
    this.showError = false;

    this.mapHelperService.FlyToCameraFast(suggestion.lat.toString(), suggestion.lng.toString(), '15', 0);
  }
}
